import PropTypes from 'prop-types';
import {
  eagerImageLoading,
  ImageLoadingContext,
  priorityImageLoading,
} from 'src/common/contexts/ImageLoadingContext';
import css from './gallery.module.scss';
import getGalleryItem from './utils/getGalleryItem';
import getNumberOfColumns from './utils/getNumberOfColumns';

const gridPctUnit = 100 / 6;
const showPeekTileWidthsBySlotCountMap = {
  1: 19,
  2: 32,
  3: 32,
};

export const imageLoadingModes = {
  // The gallery's visible tiles load eagerly.
  eager: 'eager',
  // As `eager`, and the first tile's image is also fetched at high priority (the likely LCP image).
  priority: 'priority',
};

export function CarouselItems({
  itemsMatrix,
  numRows,
  page,
  breakpoint,
  isExtendedInfoEnabled,
  showPeek,
  imageLoading,
  className = '',
}) {
  function renderRow(items, rowIndex) {
    // Width already taken by earlier tiles in this row; a tile starting under 100% is on the first page.
    let rowOffset = 0;

    return items.map((guideItem, itemsArrayIndex) => {
      const { Component: ItemComponent, requiredSlots } = getGalleryItem(guideItem);
      const itemIndex = Number.parseInt(guideItem?.index ?? itemsArrayIndex + 1, 10) - 1;
      const { tileSize } = guideItem.styles || {};
      const numberOfColumns = getNumberOfColumns(requiredSlots, breakpoint, tileSize);

      const tileWidthAsNumber = showPeek
        ? showPeekTileWidthsBySlotCountMap[requiredSlots]
        : gridPctUnit * numberOfColumns;
      const tileWidth = `${tileWidthAsNumber}%`; // as a %
      const isOnFirstPage = rowOffset < 100;
      rowOffset += tileWidthAsNumber;

      const item = (
        <ItemComponent
          guideItem={guideItem}
          index={itemIndex}
          isExtendedInfoEnabled={isExtendedInfoEnabled}
          containerClassName={css.carouselItemContainer}
        />
      );
      const isLeadItem = rowIndex === 0 && itemsArrayIndex === 0;

      return (
        <div
          data-testid={`carouselGuideItem-${itemIndex}`}
          key={`row-${rowIndex}item-${itemIndex}id-${guideItem.guideId}`}
          className={css.item}
          style={{ width: tileWidth }}
          data-guideid={guideItem.guideId}
        >
          {imageLoading && isOnFirstPage ? (
            <ImageLoadingContext.Provider
              value={
                imageLoading === imageLoadingModes.priority && isLeadItem
                  ? priorityImageLoading
                  : eagerImageLoading
              }
            >
              {item}
            </ImageLoadingContext.Provider>
          ) : (
            item
          )}
        </div>
      );
    });
  }

  return (
    <div
      data-testid="carousel"
      className={`${className} ${css.carousel} ${numRows === 1 ? css.row : ''}`}
      style={{ left: `-${page * 100}%` }}
    >
      {itemsMatrix.map((items, rowIndex) =>
        numRows > 1 ? (
          <div key={`row-${rowIndex}`} className={`${css.row} ${css.carouselRow}`}>
            {renderRow(items, rowIndex)}
          </div>
        ) : (
          renderRow(items, rowIndex)
        ),
      )}
    </div>
  );
}

CarouselItems.propTypes = {
  page: PropTypes.number.isRequired,
  itemsMatrix: PropTypes.array.isRequired,
  numRows: PropTypes.number.isRequired,
  breakpoint: PropTypes.number.isRequired,
  isExtendedInfoEnabled: PropTypes.bool,
  showPeek: PropTypes.bool,
  imageLoading: PropTypes.oneOf(Object.values(imageLoadingModes)),
  className: PropTypes.string,
};
